import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid, Receipt, ListChecks, BookOpen, BedDouble, PiggyBank, FileStack, LogIn, Wrench, Sparkles, RefreshCw, CalendarClock, Tags, DoorOpen, Scale, Ban, TrendingUp, FileSearch, Grid3x3, CalendarDays, ShieldCheck, Users, Database, Map as MapIcon, StickyNote } from 'lucide-react';
import { useToolkitProject, ToolkitProjectHeader } from './toolkit/ToolkitProject';
import ToolkitJobsList from './toolkit/ToolkitJobsList';
import Tabs from './shared/Tabs';
import ProductSupportTab, { SUPPORT_TAB, useSupportTab } from './support/ProductSupportTab';
import './OhipTransactionsPage.css';
import './ToolkitBlocksPage.css';

// Sync › project › Toolkit — the HCONNECT Migration Toolkit for ONE sync project.
//
// The desktop app (a separate Windows Electron repository) migrates Oracle OPERA v5 / on-premise data into OPERA
// Cloud through OHIP, with a technician typing OHIP settings into it. Here it is part of the sync project whose
// template uses it (synctemplates.uses_migration_toolkit = 'Y'), and every screen works through that project's own
// OPERA Cloud connection — the same connector, credential and overrides the sync uses — so nothing is configured
// twice and nothing can point at the wrong property.
//
// One tile per ported screen. Screens that read the on-premise v5 database do it through the project's SyncConnect
// (toolkit onprem.js) — a lambda cannot reach that database itself.

// Grouped as a migration is worked: the money first, then the reservations, then the building — and last, the checks
// that prove it balanced. Those are read-only: safe to run on a live property at any time.
const GROUPS = [
    { title: 'Accounts receivable', tools: [
        { path: 'ar-accounts', title: 'AR Accounts', icon: Receipt,
          text: 'Create OPERA Cloud AR accounts (and their profiles and AR addresses) from a v5 AR Accounts report or the v5 database.' },
        { path: 'ar-account-types', title: 'AR Account Types', icon: ListChecks,
          text: 'Set existing OPERA Cloud AR accounts to the type in a v5 AR Account Type report.' },
        { path: 'ar-ledger', title: 'AR Ledger', icon: BookOpen,
          text: 'Post the open invoices of a v5 AR Ledger Aging report to their OPERA Cloud AR accounts as old balances.' },
    ] },
    { title: 'Guest and deposit ledgers', tools: [
        { path: 'guest-ledger', title: 'Guest Ledger', icon: BedDouble,
          text: 'Post v5 guest balances to their OPERA Cloud reservations as charges — pre-stay and open folios included.' },
        { path: 'deposit-ledger', title: 'Deposit Ledger', icon: PiggyBank,
          text: 'Post v5 deposits to their OPERA Cloud reservations as deposit payments.' },
        { path: 'deposit-folio', title: 'Deposit Folio', icon: FileStack,
          text: 'Create deposit folios (charge and payment) on OPERA Cloud reservations from a v5 spreadsheet.' },
    ] },
    { title: 'Profiles and notes', tools: [
        { path: 'internal-notes', title: 'Internal Notes', icon: StickyNote,
          text: 'Copy v5 profile notes, internal reservation comments and block notes onto their Opera Cloud records.' },
        { path: 'udfs', title: 'UDFs', icon: Tags,
          text: 'Find the v5 profile and reservation UDFs in use, map them to Opera Cloud UDFs, and copy the values across.' },
    ] },
    { title: 'Reservations', tools: [
        { path: 'check-in', title: 'CheckIn', icon: LogIn,
          text: 'Check reservations in to their rooms from a v5 arrivals list.' },
        { path: 'same-day-departures', title: 'Same-Day Departures', icon: CalendarClock,
          text: 'Find same-day arrivals-and-departures and move them to another rate plan.' },
        { path: 'market-codes', title: 'Market Codes', icon: Tags,
          text: 'Find reservations by market code and arrival date, and change their market code.' },
        { path: 'refresh-rates', title: 'Refresh Res Rates', icon: RefreshCw,
          text: 'Make OPERA Cloud re-price reservations by moving each to a temporary rate plan and back.' },
    ] },
    { title: 'Groups and events', tools: [
        { path: 'blocks', title: 'Blocks', icon: LayoutGrid,
          text: 'Compare a v5 Block Information report against the same blocks in OPERA Cloud.' },
        { path: 'block-grid', title: 'Block Grid Update', icon: Grid3x3,
          text: 'Copy block room allocations from the source Opera Cloud to the target (Cloud → Cloud projects).' },
        { path: 'event-management', title: 'Event Management', icon: CalendarDays,
          text: 'Create Opera Cloud events from a v5 Event List report, on their blocks, with code mappings.' },
    ] },
    { title: 'Rooms and housekeeping', tools: [
        { path: 'create-rooms', title: 'Create Rooms', icon: DoorOpen,
          text: 'Create a range of rooms of one room type.' },
        { path: 'ooo', title: 'OOO / OOS', icon: Wrench,
          text: 'Put rooms out of order or out of service from a v5 Out of Order by Reason report.' },
        { path: 'hk-details', title: 'HK Details', icon: Sparkles,
          text: 'Set each room\'s housekeeping status from a v5 HK Details report.' },
    ] },
    { title: 'Balance checks (read-only)', tools: [
        { path: 'balance-reservations', title: 'Balance Reservations', icon: Scale,
          text: 'Compare the v5 and Opera Cloud Res Detail reports, reservation by reservation — and cancel what v5 cancelled.' },
        { path: 'cancellation-check', title: 'Cancellation Check', icon: Ban,
          text: 'Check every reservation cancelled in v5 is cancelled in Opera Cloud too.' },
        { path: 'history-forecast', title: 'History & Forecast', icon: TrendingUp,
          text: 'Compare the v5 and Opera Cloud History & Forecast reports, day by day.' },
        { path: 'ar-ledger-comparison', title: 'AR Ledger Comparison', icon: FileSearch,
          text: 'Compare the v5 and Opera Cloud AR Ledger Aging reports, invoice by invoice.' },
    ] },
    { title: 'PEP balancing (files stay in your browser)', tools: [
        { path: 'pep-guest-balancing', title: 'PEP Guest Balancing', icon: ShieldCheck,
          text: 'Check a PEP guest file for HTS tokens, 2099 expiries and duplicate receipts, compare it with the guest ledger, and clean it.' },
        { path: 'pep-group-master', title: 'PEP Group Master', icon: Users,
          text: 'Compare the PEP group master file with the Opera v5 Block Info export.' },
    ] },
    // Read from the property's OPERA v5 database through the project's SyncConnect (toolkit onprem.js). One page,
    // one catalogue key each — the keys must match the catalogue on the server.
    { title: 'On-premise database (through SyncConnect)', tools: [
        ['profile-notes', 'Profile Notes', 'Profile notes at the property — text, type, internal or not.'],
        ['reservation-comments', 'Reservation Comments', 'How many reservation comments there are, by type and internal / external.'],
        ['ar-accounts', 'AR Accounts (v5)', 'The property\'s AR accounts in v5, with balance and credit limit.'],
        ['reservations', 'Future Reservations', 'Reservations arriving from the business date on.'],
        ['blocks', 'Blocks (v5)', 'The property\'s blocks in v5, with status, market and source.'],
        ['stay-history', 'Stay History', 'Checked-out stays, most recent first.'],
        ['udfs', 'Profile UDFs in Use', 'Which profile UDFs carry values — the ones to map before migrating.'],
        ['profiles-from-stays', 'Profiles from Stays', 'Every guest, company, agent, source and group profile on stays departed between two dates.'],
        ['ifc-sweep', 'IFC Sweep', 'Every active interface — name, protocol, comms type and last communication.'],
        ['oxi-interfaces', 'OXI Interfaces', 'The property\'s active OPERA OXI interfaces.'],
        ['oxi-queues-by-state', 'OXI Queue by State', 'Messages in OXI\'s inbound queue, by interface and state.'],
        ['oxi-queues-by-message-type', 'OXI Queue by Message Type', 'The inbound queue by message type and state.'],
    ].map(([key, title, text]) => ({ path: `onprem/${key}`, title, icon: Database, text })).concat([
        { path: 'config-compare', title: 'v5 vs Cloud Config', icon: Scale,
          text: 'Compare v5 configuration codes (account, address, ID, guarantee, payment, membership…) with Opera Cloud and list the gaps.' },
        { path: 'oxi-mapping', title: 'OXI Mapping', icon: MapIcon,
          text: 'Check every HCONNECT OXI conversion code maps to a value that exists, and is active, in Opera Cloud.' },
    ]) },
    // These two WRITE to the property's v5 database (toolkit onprem.js WRITES) — superadmin, confirmed by name.
    { title: 'On-premise database — changes (writes to v5)', tools: [
        { path: 'profile-resync', title: 'Profile Re-sync', icon: RefreshCw,
          text: 'Re-send a date range\'s profiles from v5 to Opera Cloud through OXI (stamps a free UDF and saves each profile).' },
        { path: 'hconnect-oxi', title: 'HConnect OXI', icon: ShieldCheck,
          text: 'Check the HConnect OXI interface at the property, and install its licence.' },
    ] },
];

function ToolkitPage() {
    const navigate = useNavigate();
    const toolkit = useToolkitProject();
    const [tab, setTab] = useSupportTab('tools');   // Tools | Support
    const { project } = toolkit;

    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} isOverview title="Migration Toolkit"
                subtitle="Migrate Opera v5 and on-premise data into this project's OPERA Cloud property." />

            {toolkit.ready && (
                <Tabs tabs={[{ key: 'tools', label: 'Tools', icon: <Wrench size={20} /> }, SUPPORT_TAB]} active={tab} onChange={setTab} />
            )}

            {/* The Toolkit's tickets: about this sync project, raised from the Toolkit (product Toolkit, id the sync id). */}
            {toolkit.ready && tab === 'support' && (
                <ProductSupportTab product="Toolkit" productId={toolkit.syncId} noun="this project's Toolkit"
                    context={{ screen: 'toolkit', syncId: toolkit.syncId, projectName: project.projectName,
                        target: project.targetConnectorName || project.targetSystemName, hotel: project.hotelName }} />
            )}

            {toolkit.ready && tab === 'tools' && <ToolkitJobsList ctx={toolkit.ctx} />}

            {toolkit.ready && tab === 'tools' && GROUPS.map((g) => (
                <section key={g.title} className="tkb-group">
                    <h2 className="tkb-group-title">{g.title}</h2>
                    <div className="tkb-tiles">
                        {g.tools.map(({ path, title, icon: Icon, text }) => (
                            <button key={path} type="button" className="tkb-tile"
                                onClick={() => navigate(`/sync/${toolkit.syncId}/toolkit/${path}`)}>
                                <Icon size={22} className="tkb-tile-icon" />
                                <span className="tkb-tile-title">{title}</span>
                                <span className="tkb-tile-text">{text}</span>
                            </button>
                        ))}
                    </div>
                </section>
            ))}
        </div>
    );
}

export default ToolkitPage;
